import { ScreenSharePresets, Track } from 'livekit-client'
import type {
  ScreenShareCaptureOptions,
  TrackPublishDefaults,
  TrackPublishOptions,
} from 'livekit-client'

/**
 * Возвращает согласованные параметры захвата и публикации одного слоя экрана.
 *
 * Кодек и отключение simulcast предназначены только для ScreenShare, не для камер.
 *
 * @param highQuality — включён ли профиль с пределом 1080p вместо 720p
 * @param videoCodec — основной кодек демонстрации экрана
 */
export function getScreenShareProfile(highQuality = true, videoCodec: 'vp8' | 'h264' = 'vp8'): {
  captureOptions: ScreenShareCaptureOptions
  publishOptions: TrackPublishOptions
} {
  const preset = highQuality ? ScreenSharePresets.h1080fps15 : ScreenSharePresets.h720fps15

  return {
    captureOptions: {
      audio: true,
      selfBrowserSurface: 'include',
      resolution: preset.resolution,
    },
    publishOptions: {
      videoCodec,
      backupCodec: videoCodec === 'h264' ? { codec: 'vp8' } : false,
      screenShareEncoding: preset.encoding,
      screenShareSimulcastLayers: [],
      simulcast: false,
    },
  }
}

/**
 * Выбирает профиль экрана: URL-переопределение важнее строгого boolean-флага API.
 *
 * Отсутствующие и некорректные настройки сохраняют повышенное качество и VP8 по умолчанию.
 * Флаг API снижает разрешение независимо от URL-переопределения, кодек при этом всегда VP8.
 *
 * @param settings — настройки встречи, полученные при входе
 * @param hq — значение URL-параметра hq; при повторении берём первое
 */
export function resolveScreenShareProfile(
  settings?: { force_lower_share_screen_quality?: unknown } | null,
  hq?: string | string[],
): ReturnType<typeof getScreenShareProfile> {
  const forceHighQuality = (Array.isArray(hq) ? hq[0] : hq) === 'true'
  const forceLowerQuality = settings?.force_lower_share_screen_quality === true

  // H.264 по флагу отключён: временный фикс для Квантaстики, кодек всегда VP8.
  return getScreenShareProfile(forceHighQuality || !forceLowerQuality, 'vp8')
}

/**
 * Выбирает доступный кодек и запрещает дополнительные слои только для экрана.
 *
 * Явные параметры не дают defaults комнаты или SDK восстановить simulcast.
 *
 * @param defaults — параметры публикации комнаты
 * @param options — отдельные параметры демонстрации экрана
 */
export function getScreenShareTrackPublishOptions(
  defaults: TrackPublishDefaults,
  options: TrackPublishOptions,
): TrackPublishOptions {
  const mergedOptions = { ...defaults, ...options }
  const codecs = typeof RTCRtpSender !== 'undefined'
    ? RTCRtpSender.getCapabilities?.('video')?.codecs
    : undefined
  // Раньше отсутствие списка кодеков считалось поддержкой H.264 и старый браузер
  // получал поток, который не мог разобрать. Нет данных — значит VP8
  const supportsH264 = Boolean(
    codecs?.some((codec) => codec.mimeType.toLowerCase() === 'video/h264'),
  )

  return {
    ...mergedOptions,
    source: Track.Source.ScreenShare,
    videoCodec: mergedOptions.videoCodec === 'h264' && supportsH264 ? 'h264' : 'vp8',
    screenShareSimulcastLayers: [],
    simulcast: false,
  }
}
